import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { clearToken, fetchMe, getToken, loginRequest, setToken } from "@/lib/api";
import { setTimeZone } from "@/lib/time";

const AuthContext = createContext(null);

// Map a URL path to the page key used for permissions (null = any signed-in user, "admin" = admins only)
export const pageKeyForPath = (pathname) => {
  if (pathname === "/") return "overview";
  const seg = pathname.split("/")[1];
  if (seg === "users") return "admin";
  if (seg === "dispatcher") return "dispatcher-leaderboard";
  if (seg === "alvys" && pathname.startsWith("/alvys/load/")) return null;
  if (seg === "reports") return "admin";
  return seg;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getToken() ? null : false);
  setTimeZone(user ? user.timezone : undefined);

  useEffect(() => {
    if (getToken()) fetchMe().then(setUser).catch(() => setUser(false));
    const onExpired = () => setUser(false);
    window.addEventListener("auth-expired", onExpired);
    return () => window.removeEventListener("auth-expired", onExpired);
  }, []);

  const login = useCallback(async (email, password, remember = false) => {
    const { token, user: u } = await loginRequest(email, password, remember);
    setToken(token, remember);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(false);
  }, []);

  const refresh = useCallback(() => fetchMe().then(setUser), []);

  const canAccess = useCallback((key) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (key === "admin") return false;
    return key == null || user.pages.includes(key);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, canAccess, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
